import { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, CheckCircle2, MousePointerClick } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TutorialAnimatedDemo } from '@/components/TutorialAnimatedDemo';
import type { Tutorial, TutorialStep } from '@/lib/tutorial-data';

interface TutorialOverlayProps {
  tutorial: Tutorial;
  onClose: () => void;
  onComplete: (tutorialId: string) => void;
}

export function TutorialOverlay({ tutorial, onClose, onComplete }: TutorialOverlayProps) {
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const current = tutorial.steps[step];
  const isLast = step === tutorial.steps.length - 1;

  const findTarget = useCallback(() => {
    if (!current.target) { setTargetRect(null); return; }
    const el = document.querySelector(current.target);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setTargetRect(el.getBoundingClientRect());
    } else {
      setTargetRect(null);
    }
  }, [current.target]);

  useEffect(() => {
    findTarget();
    const id = setInterval(findTarget, 500);
    return () => clearInterval(id);
  }, [findTarget]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && !isLast) setStep(s => s + 1);
      if (e.key === 'ArrowLeft' && step > 0) setStep(s => s - 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [step, isLast, onClose]);

  const handleNext = () => {
    if (isLast) { onComplete(tutorial.id); onClose(); }
    else setStep(s => s + 1);
  };

  // Rectangular spotlight cutout
  const spotPad = 12;
  const spotX = targetRect ? targetRect.left - spotPad : 0;
  const spotY = targetRect ? targetRect.top - spotPad : 0;
  const spotW = targetRect ? targetRect.width + spotPad * 2 : 0;
  const spotH = targetRect ? targetRect.height + spotPad * 2 : 0;

  // Tooltip positioning
  const pad = 16;
  let tooltipStyle: React.CSSProperties = {};
  if (targetRect && current.position !== 'center') {
    const pos = current.position;
    if (pos === 'bottom') tooltipStyle = { top: targetRect.bottom + pad + spotPad, left: Math.max(16, targetRect.left + targetRect.width / 2 - 190), maxWidth: 380 };
    else if (pos === 'top') tooltipStyle = { bottom: window.innerHeight - targetRect.top + pad + spotPad, left: Math.max(16, targetRect.left + targetRect.width / 2 - 190), maxWidth: 380 };
    else if (pos === 'right') tooltipStyle = { top: Math.max(16, targetRect.top + targetRect.height / 2 - 80), left: targetRect.right + pad + spotPad, maxWidth: 340 };
    else if (pos === 'left') tooltipStyle = { top: Math.max(16, targetRect.top + targetRect.height / 2 - 80), right: window.innerWidth - targetRect.left + pad + spotPad, maxWidth: 340 };
  }

  return (
    <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal>
      {/* SVG backdrop with circular cutout */}
      <svg className="absolute inset-0 w-full h-full" onClick={onClose}>
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={spotX}
                y={spotY}
                width={spotW}
                height={spotH}
                rx={12}
                fill="black"
              />
            )}
          </mask>
        </defs>
        {/* Dark overlay with sharp cutout */}
        <rect
          width="100%"
          height="100%"
          fill="hsl(var(--foreground) / 0.75)"
          mask="url(#spotlight-mask)"
        />
        {/* Bright border around the clear area */}
        {targetRect && (
          <rect
            x={spotX}
            y={spotY}
            width={spotW}
            height={spotH}
            rx={12}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            opacity={0.8}
          />
        )}
      </svg>

      {/* Click-here indicator near spotlight */}
      {targetRect && (
        <div
          className="absolute pointer-events-none animate-bounce"
          style={{
            top: spotY + spotH + 4,
            left: spotX + spotW - 12,
          }}
        >
          <MousePointerClick className="h-5 w-5 text-primary drop-shadow-lg" />
        </div>
      )}

      {/* Animated demo overlay */}
      {current.demo && (
        <TutorialAnimatedDemo
          sequence={current.demo}
          targetRect={targetRect}
          active={true}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className={cn(
          'absolute bg-card border border-border rounded-xl shadow-2xl p-5 animate-in fade-in-0 zoom-in-95 duration-200',
          current.position === 'center' || !targetRect
            ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-md w-[90vw]'
            : ''
        )}
        style={current.position !== 'center' && targetRect ? tooltipStyle : undefined}
      >
        {/* Step progress */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              {step + 1} / {tutorial.steps.length}
            </span>
            <div className="flex gap-1">
              {tutorial.steps.map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1 rounded-full transition-all duration-300',
                    i === step ? 'w-6 bg-primary' : i < step ? 'w-2 bg-primary/50' : 'w-2 bg-muted-foreground/20'
                  )}
                />
              ))}
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors -mr-1">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tutorial name */}
        <p className="text-[10px] uppercase tracking-widest text-primary font-semibold mb-1">{tutorial.title}</p>
        <h3 className="text-base font-semibold text-foreground mb-2">{current.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-5">{current.content}</p>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 0}
            className="gap-1 text-xs"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Back
          </Button>
          <Button size="sm" onClick={handleNext} className="gap-1 text-xs">
            {isLast ? (
              <>Complete <CheckCircle2 className="h-3.5 w-3.5" /></>
            ) : (
              <>Next <ChevronRight className="h-3.5 w-3.5" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}